// src/hooks/useDevice.js
import { useEffect, useMemo, useRef, useState } from "react";

export function useDevice() {
  const rafRef = useRef(null);
  const settleRef = useRef(null);

  // Live media queries
  const coarseMql = useMemo(
    () =>
      typeof window !== "undefined"
        ? window.matchMedia("(pointer: coarse)")
        : null,
    []
  );
  const portraitMql = useMemo(
    () =>
      typeof window !== "undefined"
        ? window.matchMedia("(orientation: portrait)")
        : null,
    []
  );

  const measure = () => {
    if (typeof window === "undefined") {
      return {
        isMobile: false,
        isDesktop: true,
        isPortrait: false,
        isLandscape: false,
        isTouch: false,
        width: 0,
        height: 0,
      };
    }

    // Prefer visualViewport on mobile (ignores URL bar jitter better)
    const vv = window.visualViewport;
    const width = Math.round(vv?.width || window.innerWidth);
    const height = Math.round(vv?.height || window.innerHeight);

    const isPortrait = portraitMql?.matches ?? height > width;
    const isLandscape = !isPortrait;

    // UA + width heuristic for mobile
    // const ua = navigator.userAgent || navigator.vendor || window.opera;
    // const uaMobile =
    //   /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    //     ua.toLowerCase()
    //   );

    // Touch detection (live via mql + fallback)
    const coarsePointer = coarseMql?.matches ?? false;
    const maxTouch = (navigator.maxTouchPoints || 0) > 0;
    const ontouch = "ontouchstart" in window;
    const isTouch = coarsePointer || maxTouch || ontouch;
    const smallScreen = width < 768;

    // iPad / Tablet in portrait mode
    const tabletPortrait = isTouch && isPortrait && width <= 1024;

    // Final decision
    const isReallyMobile = smallScreen || tabletPortrait;
    const isMobile = isReallyMobile;

    // =================================================================
    // NEW: iOS Detection Logic
    // =================================================================
    const isIOS = (() => {
      // Check for common iOS indicators (iPhone, iPad, iPod)
      const basicIOSCheck = /iPad|iPhone|iPod/.test(navigator.userAgent);
      // Check for modern iPadOS detection (Mac-like user agent with touch capability)
      const isMacWithTouch =
        navigator.userAgent.includes("Mac") && "ontouchend" in document;

      return basicIOSCheck || isMacWithTouch;
    })();

    return {
      isMobile,
      isDesktop: !isReallyMobile,
      isPortrait,
      isLandscape,
      isTouch,
      isIOS,
      width,
      height,
    };
  };

  const [device, setDevice] = useState(measure);

  // Schedule a stable update after layout settles
  const scheduleUpdate = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (settleRef.current) clearTimeout(settleRef.current);

    rafRef.current = requestAnimationFrame(() => {
      // iOS sometimes needs one more tick
      settleRef.current = setTimeout(() => {
        setDevice(measure());
      }, 0);
    });
  };

  useEffect(() => {
    const onResize = scheduleUpdate;
    const onOrientation = scheduleUpdate;
    const onVVResize = scheduleUpdate;

    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onOrientation, {
      passive: true,
    });

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", onVVResize, {
        passive: true,
      });
    }

    // media query listeners (coarse pointer + orientation)
    const onMqlChange = scheduleUpdate;
    coarseMql?.addEventListener?.("change", onMqlChange);
    portraitMql?.addEventListener?.("change", onMqlChange);

    // initial settle (in case of immediate orientation)
    scheduleUpdate();

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrientation);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", onVVResize);
      }

      coarseMql?.removeEventListener?.("change", onMqlChange);
      portraitMql?.removeEventListener?.("change", onMqlChange);

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (settleRef.current) clearTimeout(settleRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // listeners only once

  return device;
}
